import { MONDAY, addDays, compareDates, requireIsoDate, toDayNumber, weekdayOf, yearOf, } from './date';
import { DEFAULT_BUNDESLAND, holidaysForYears, } from './holidays';
const DEFAULT_WORKING_WEEKDAYS = [1, 2, 3, 4, 5];
/**
 * Upper bound for the search loops below.
 *
 * A calendar whose every day is blocked would otherwise loop forever. Ten
 * thousand calendar days is roughly 27 years: far beyond any real plan, close
 * enough to fail fast on an absurd calendar.
 */
const MAX_SEARCH_DAYS = 10_000;
export function createWorkCalendar(input = {}) {
    const workingWeekdays = new Set(input.workingWeekdays ?? DEFAULT_WORKING_WEEKDAYS);
    if (workingWeekdays.size === 0) {
        // Without a single working weekday no duration could ever elapse.
        throw new Error('A work calendar needs at least one working weekday.');
    }
    const nonWorkingDates = new Set();
    if (input.includeHolidays !== false) {
        const currentYear = new Date().getUTCFullYear();
        const span = input.holidayYears ?? {
            from: currentYear - 2,
            to: currentYear + 10,
        };
        const holidays = holidaysForYears(input.bundesland ?? DEFAULT_BUNDESLAND, span.from, span.to, input.holidayOptions ?? {});
        for (const holiday of holidays)
            nonWorkingDates.add(holiday.date);
    }
    for (const date of input.additionalNonWorkingDates ?? []) {
        nonWorkingDates.add(requireIsoDate(date));
    }
    const extraWorkingDates = new Set();
    for (const date of input.extraWorkingDates ?? []) {
        extraWorkingDates.add(requireIsoDate(date));
    }
    return { workingWeekdays, nonWorkingDates, extraWorkingDates };
}
/**
 * Is this a working day?
 *
 * Order of precedence: an explicit extra working day wins over everything,
 * then a blocked date, then the weekday rule.
 */
export function isWorkingDay(calendar, date) {
    if (calendar.extraWorkingDates.has(date))
        return true;
    if (calendar.nonWorkingDates.has(date))
        return false;
    return calendar.workingWeekdays.has(weekdayOf(date));
}
/** The date itself if it is a working day, otherwise the next one forward. */
export function nextWorkingDay(calendar, date) {
    let current = date;
    for (let step = 0; step <= MAX_SEARCH_DAYS; step++) {
        if (isWorkingDay(calendar, current))
            return current;
        current = addDays(current, 1);
    }
    throw new Error(`No working day found within ${MAX_SEARCH_DAYS} days after ${date}. ` +
        'The calendar blocks every day.');
}
/** The date itself if it is a working day, otherwise the previous one. */
export function previousWorkingDay(calendar, date) {
    let current = date;
    for (let step = 0; step <= MAX_SEARCH_DAYS; step++) {
        if (isWorkingDay(calendar, current))
            return current;
        current = addDays(current, -1);
    }
    throw new Error(`No working day found within ${MAX_SEARCH_DAYS} days before ${date}. ` +
        'The calendar blocks every day.');
}
/**
 * Moves by whole working days.
 *
 * The start date is first aligned onto a working day - forward for a
 * non-negative count, backward for a negative one - and the requested number of
 * working days is then stepped off.
 *
 * Consequences worth knowing:
 *   addWorkingDays(cal, <a Monday>, 0)   === that Monday
 *   addWorkingDays(cal, <a Saturday>, 0) === the following Monday
 *   addWorkingDays(cal, <a Saturday>, -1) === the Thursday before
 *
 * A task of duration `d` working days starting on `s` therefore ends on
 * addWorkingDays(cal, s, d - 1).
 */
export function addWorkingDays(calendar, date, count) {
    if (!Number.isInteger(count)) {
        throw new Error(`Working days must be a whole number, got ${count}.`);
    }
    const forward = count >= 0;
    let current = forward
        ? nextWorkingDay(calendar, date)
        : previousWorkingDay(calendar, date);
    let remaining = Math.abs(count);
    let guard = 0;
    while (remaining > 0) {
        current = addDays(current, forward ? 1 : -1);
        if (isWorkingDay(calendar, current))
            remaining--;
        if (++guard > MAX_SEARCH_DAYS) {
            throw new Error(`Could not step ${count} working days from ${date} within ` +
                `${MAX_SEARCH_DAYS} calendar days. The calendar blocks too much.`);
        }
    }
    return current;
}
/**
 * Working days in the half-open interval [from, to).
 *
 * Half-open on purpose, so that it is the exact inverse of
 * {@link addWorkingDays} on a working day:
 *
 *   workingDaysBetween(cal, a, addWorkingDays(cal, a, n)) === n
 *
 * Returns a negative count when `to` lies before `from`.
 */
export function workingDaysBetween(calendar, from, to) {
    const direction = compareDates(from, to);
    if (direction === 0)
        return 0;
    const [start, end] = direction < 0 ? [from, to] : [to, from];
    const endNumber = toDayNumber(end);
    let count = 0;
    let current = start;
    while (toDayNumber(current) < endNumber) {
        if (isWorkingDay(calendar, current))
            count++;
        current = addDays(current, 1);
    }
    return direction < 0 ? count : -count;
}
/** Working days in a closed interval, as a user would count "days on site". */
export function workingDaysInclusive(calendar, from, to) {
    if (compareDates(from, to) > 0)
        return 0;
    return workingDaysBetween(calendar, from, addDays(to, 1));
}
/**
 * End date of a task, given its start and its duration in working days.
 *
 * A duration of zero marks a milestone: it has no extent, so its date is the
 * start aligned onto a working day.
 */
export function endOfTask(calendar, start, durationDays) {
    if (durationDays < 0 || !Number.isInteger(durationDays)) {
        throw new Error(`Duration must be a whole number >= 0, got ${durationDays}.`);
    }
    const alignedStart = nextWorkingDay(calendar, start);
    if (durationDays === 0)
        return alignedStart;
    return addWorkingDays(calendar, alignedStart, durationDays - 1);
}
/**
 * Start date of a task, given its finish and its duration in working days.
 * The mirror image of {@link endOfTask}, used by the backward pass.
 */
export function startOfTask(calendar, finish, durationDays) {
    if (durationDays < 0 || !Number.isInteger(durationDays)) {
        throw new Error(`Duration must be a whole number >= 0, got ${durationDays}.`);
    }
    const alignedFinish = previousWorkingDay(calendar, finish);
    if (durationDays <= 1)
        return alignedFinish;
    return addWorkingDays(calendar, alignedFinish, -(durationDays - 1));
}
/** Convenience for the common case: Monday to Friday in one federal state. */
export function standardCalendar(bundesland = DEFAULT_BUNDESLAND, holidayYears) {
    return createWorkCalendar({
        workingWeekdays: [MONDAY, 2, 3, 4, 5],
        bundesland,
        ...(holidayYears ? { holidayYears } : {}),
    });
}
/** The year span a calendar was built for, useful for diagnostics. */
export function holidayYearsOf(calendar) {
    return [...new Set([...calendar.nonWorkingDates].map(yearOf))].sort((a, b) => a - b);
}
export function calendarFromSettings(settings) {
    const year = Number(settings.referenceDate.slice(0, 4));
    return createWorkCalendar({
        workingWeekdays: settings.workingWeekdays,
        bundesland: settings.bundesland,
        // A generous span, so a plan running several years does not silently lose
        // its holidays at the edge.
        holidayYears: { from: year - 1, to: year + 15 },
        holidayOptions: {
            fronleichnam: settings.fronleichnam,
            mariaeHimmelfahrt: settings.mariaeHimmelfahrt,
        },
        additionalNonWorkingDates: settings.exceptions
            .filter((exception) => exception.kind === 'off')
            .map((exception) => exception.day),
        extraWorkingDates: settings.exceptions
            .filter((exception) => exception.kind === 'on')
            .map((exception) => exception.day),
    });
}
//# sourceMappingURL=calendar.js.map